/**
 * Persistencia del estado del BulkUploader.
 *
 * Dos capas:
 *  1. localStorage: resumen ligero del batch (sin Files). Sirve para
 *     mostrar el banner de recovery sin tocar IDB.
 *  2. IndexedDB: store de File objects + items completos. Permite
 *     reanudar exactamente donde se quedó, aunque el navegador se cierre.
 *
 * Limitación conocida: cuando un File se persiste en IDB, el navegador
 * mantiene una referencia al disco. Si el usuario mueve/borra/edita el
 * archivo original entre sesiones, al re-leer dará NotFoundError /
 * NotReadableError. Lo manejamos como un error de upload normal.
 */

const LS_KEY = 'daysi.bulkUploader.batches';
const DB_NAME = 'daysi-bulk-uploader';
const DB_VERSION = 1;
const STORE_BATCHES = 'batches';   // resumen + meta del batch
const STORE_ITEMS = 'items';       // items con archivos persistidos
const MAX_BATCH_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

// ─── localStorage helpers (rápido, sin async) ───────────────────────
const lsList = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const lsSave = (map) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch {}
};

export const summarySave = (batchId, summary) => {
  const map = lsList();
  map[batchId] = { ...summary, updatedAt: Date.now() };
  lsSave(map);
};

export const summaryGet = (batchId) => {
  const map = lsList();
  return map[batchId] || null;
};

export const summaryGetAll = () => {
  const map = lsList();
  // Filtra los muy viejos (>7d)
  const now = Date.now();
  const cleaned = {};
  Object.entries(map).forEach(([id, s]) => {
    if (s?.updatedAt && (now - s.updatedAt) < MAX_BATCH_AGE_MS) {
      cleaned[id] = s;
    }
  });
  if (Object.keys(cleaned).length !== Object.keys(map).length) {
    lsSave(cleaned);
  }
  return Object.entries(cleaned)
    .map(([id, s]) => ({ batchId: id, ...s }))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
};

export const summaryRemove = (batchId) => {
  const map = lsList();
  delete map[batchId];
  lsSave(map);
};

// ─── IndexedDB helpers (lazy, sólo cuando se necesita) ──────────────
let dbPromise = null;

const openDB = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no está disponible en este navegador.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_BATCHES)) {
        db.createObjectStore(STORE_BATCHES, { keyPath: 'batchId' });
      }
      if (!db.objectStoreNames.contains(STORE_ITEMS)) {
        const store = db.createObjectStore(STORE_ITEMS, { keyPath: 'id' });
        store.createIndex('batchId', 'batchId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IDB open failed'));
  });
  return dbPromise;
};

const tx = async (storeNames, mode = 'readonly') => {
  const db = await openDB();
  return db.transaction(storeNames, mode);
};

const reqAsync = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

// Guarda meta del batch (sin archivos)
export const idbSaveBatch = async (batchId, meta) => {
  try {
    const t = await tx([STORE_BATCHES], 'readwrite');
    await reqAsync(t.objectStore(STORE_BATCHES).put({
      batchId,
      ...meta,
      updatedAt: Date.now()
    }));
  } catch (e) {
    console.warn('idbSaveBatch falló:', e?.message);
  }
};

export const idbLoadBatch = async (batchId) => {
  try {
    const t = await tx([STORE_BATCHES]);
    return await reqAsync(t.objectStore(STORE_BATCHES).get(batchId));
  } catch { return null; }
};

export const idbListBatches = async () => {
  try {
    const t = await tx([STORE_BATCHES]);
    return await reqAsync(t.objectStore(STORE_BATCHES).getAll());
  } catch { return []; }
};

export const idbDeleteBatch = async (batchId) => {
  try {
    const t = await tx([STORE_BATCHES, STORE_ITEMS], 'readwrite');
    await reqAsync(t.objectStore(STORE_BATCHES).delete(batchId));
    // Borrar todos los items del batch
    const itemsStore = t.objectStore(STORE_ITEMS);
    const idx = itemsStore.index('batchId');
    const items = await reqAsync(idx.getAllKeys(batchId));
    for (const key of items) await reqAsync(itemsStore.delete(key));
  } catch (e) {
    console.warn('idbDeleteBatch falló:', e?.message);
  }
};

// Persiste un lote de items (incluyendo `file` File object)
export const idbSaveItems = async (batchId, items) => {
  if (!items?.length) return;
  try {
    const t = await tx([STORE_ITEMS], 'readwrite');
    const store = t.objectStore(STORE_ITEMS);
    for (const it of items) {
      // File es persistible en IDB (StructuredClone-compatible en navegadores modernos)
      store.put({ id: it.id, batchId, ...it });
    }
    await new Promise((resolve, reject) => {
      t.oncomplete = resolve;
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  } catch (e) {
    console.warn('idbSaveItems falló:', e?.message);
  }
};

export const idbUpdateItemStatus = async (id, patch) => {
  try {
    const t = await tx([STORE_ITEMS], 'readwrite');
    const store = t.objectStore(STORE_ITEMS);
    const cur = await reqAsync(store.get(id));
    if (!cur) return;
    await reqAsync(store.put({ ...cur, ...patch }));
  } catch (e) { /* silent */ }
};

export const idbLoadItems = async (batchId) => {
  try {
    const t = await tx([STORE_ITEMS]);
    const idx = t.objectStore(STORE_ITEMS).index('batchId');
    return await reqAsync(idx.getAll(batchId));
  } catch { return []; }
};

export const idbDeleteItem = async (id) => {
  try {
    const t = await tx([STORE_ITEMS], 'readwrite');
    await reqAsync(t.objectStore(STORE_ITEMS).delete(id));
  } catch { /* silent */ }
};

// Purga batches viejos en IDB (>7d)
export const idbPurgeOld = async () => {
  try {
    const batches = await idbListBatches();
    const now = Date.now();
    for (const b of batches) {
      if (b.updatedAt && (now - b.updatedAt) > MAX_BATCH_AGE_MS) {
        await idbDeleteBatch(b.batchId);
      }
    }
  } catch (e) { /* silent */ }
};

// Helper: detectar si el navegador soporta lo necesario
export const isPersistenceSupported = () => {
  return typeof indexedDB !== 'undefined' && typeof localStorage !== 'undefined';
};
